// pages/Agents/AgentSpace/AgentSpace.js
import React, { useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useParams } from 'react-router-dom';
import { useChatbots } from '../../../../contexts/ChatbotContext';
import AgentSpaceSidebar from './AgentSpaceSidebar';
import AgentSpaceChat from './AgentSpaceChat';
import AgentSpaceDashboard from './AgentSpaceDashboard';
import AgentSpaceConsole from './AgentSpaceConsole';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
};

export default function AgentSpace() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const { chatbotId } = useParams();
    const { activeChatbot } = useChatbots();

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [currentSessionCode, setCurrentSessionCode] = useState(null);
    const [activeView, setActiveView] = useState('chat');
    const [agentTypeFilter, setAgentTypeFilter] = useState(null);
    const [createTaskOpen, setCreateTaskOpen] = useState(false);

    const handleInitialAgentType = (firstType) => {
        if (!agentTypeFilter) setAgentTypeFilter(firstType);
    };

    const handleConversationSelect = (sessionCode) => {
        setCurrentSessionCode(sessionCode || null);
        setSidebarOpen(false);
    };

    const handleNewTask = () => {
        setCurrentSessionCode(null);
        setActiveView('chat');
        setSidebarOpen(false);
        setCreateTaskOpen(true);
    };

    return (
        <Box sx={{ fontFamily, maxWidth: '1200px', width: '100%', height: { xs: 'calc(100vh - 80px)', sm: 'calc(100vh - 120px)' }, mx: 'auto', display: 'flex', flexDirection: 'column' }}>

            {/* Toolbar */}
            <Box sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: { xs: '10px', sm: '14px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 0.75, sm: 1 },
                mb: { xs: 1, sm: 2 },
                flexShrink: 0,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {isMobile && (
                        <IconButton
                            size="small"
                            onClick={() => setSidebarOpen(true)}
                            sx={{ color: colors.textTertiary, '&:hover': { bgcolor: 'rgba(139,92,246,0.1)', color: colors.accent } }}
                        >
                            <MenuIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    )}
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary }}>
                        {activeView === 'console' ? 'Agent Console' : activeView === 'dashboard' ? 'Agent Insights' : currentSessionCode ? 'Active Task' : 'New Task'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', bgcolor: 'rgba(0,0,0,0.04)', borderRadius: '10px', p: '3px' }}>
                    <Button
                        size="small"
                        startIcon={<ChatOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => { setActiveView('chat'); setAgentTypeFilter(null); }}
                        sx={{
                            fontFamily,
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            px: { xs: 1.25, sm: 2 },
                            py: 0.5,
                            minWidth: 'auto',
                            bgcolor: activeView === 'chat' ? '#fff' : 'transparent',
                            color: activeView === 'chat' ? colors.accent : colors.textTertiary,
                            boxShadow: activeView === 'chat' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': { bgcolor: activeView === 'chat' ? '#fff' : 'rgba(0,0,0,0.04)' },
                            '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Conversations</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Chats</Box>
                    </Button>
                    <Button
                        size="small"
                        startIcon={<DashboardOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => setActiveView('dashboard')}
                        sx={{
                            fontFamily,
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            px: { xs: 1.25, sm: 2 },
                            py: 0.5,
                            minWidth: 'auto',
                            bgcolor: activeView === 'dashboard' ? '#fff' : 'transparent',
                            color: activeView === 'dashboard' ? colors.accent : colors.textTertiary,
                            boxShadow: activeView === 'dashboard' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': { bgcolor: activeView === 'dashboard' ? '#fff' : 'rgba(0,0,0,0.04)' },
                            '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                        }}
                    >
                        Dashboard
                    </Button>
                    <Button
                        size="small"
                        startIcon={<TerminalIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => setActiveView('console')}
                        sx={{
                            fontFamily,
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            px: { xs: 1.25, sm: 2 },
                            py: 0.5,
                            minWidth: 'auto',
                            bgcolor: activeView === 'console' ? '#fff' : 'transparent',
                            color: activeView === 'console' ? colors.accent : colors.textTertiary,
                            boxShadow: activeView === 'console' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': { bgcolor: activeView === 'console' ? '#fff' : 'rgba(0,0,0,0.04)' },
                            '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 1 } },
                        }}
                    >
                        Console
                    </Button>
                </Box>
            </Box>

            {/* Main Area */}
            <Box sx={{ display: 'flex', flex: 1, width: '100%', overflow: 'hidden', gap: { xs: 0, lg: 2 }, minHeight: 0 }}>
                {/* LEFT SIDEBAR — hidden in console view */}
                {!isMobile && activeView !== 'console' && (
                    <AgentSpaceSidebar
                        isOpen={true}
                        isCollapsed={sidebarCollapsed}
                        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                        onConversationSelect={handleConversationSelect}
                        currentSessionCode={currentSessionCode}
                        chatbotId={chatbotId}
                        activeView={activeView}
                        agentTypeFilter={agentTypeFilter}
                        onResourceClick={(type) => { setActiveView('dashboard'); setAgentTypeFilter(type); }}
                        onInitialAgentType={handleInitialAgentType}
                        onNewTask={handleNewTask}
                    />
                )}
                {isMobile && activeView !== 'console' && (
                    <AgentSpaceSidebar
                        isOpen={sidebarOpen}
                        isCollapsed={false}
                        onToggle={() => setSidebarOpen(!sidebarOpen)}
                        onConversationSelect={handleConversationSelect}
                        currentSessionCode={currentSessionCode}
                        chatbotId={chatbotId}
                        isMobile
                        activeView={activeView}
                        agentTypeFilter={agentTypeFilter}
                        onResourceClick={(type) => { setActiveView('dashboard'); setAgentTypeFilter(type); }}
                        onInitialAgentType={handleInitialAgentType}
                        onNewTask={handleNewTask}
                    />
                )}

                {/* MAIN CONTENT */}
                <Box sx={{ flex: 1, overflow: 'hidden', borderRadius: '16px' }}>
                    {activeView === 'console' ? (
                        <AgentSpaceConsole chatbotId={chatbotId} />
                    ) : activeView === 'dashboard' ? (
                        <AgentSpaceDashboard chatbotId={chatbotId} agentTypeFilter={agentTypeFilter} onFilterChange={setAgentTypeFilter} />
                    ) : (
                        <AgentSpaceChat
                            sessionCode={currentSessionCode}
                            onSessionChange={setCurrentSessionCode}
                            chatbotId={chatbotId}
                            createTaskOpen={createTaskOpen}
                            setCreateTaskOpen={setCreateTaskOpen}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
}
